import { styled } from '@mui/material';
import {
  type DateRange,
  DateRangePicker,
  LocalizationProvider,
  SingleInputDateRangeField,
} from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { parseISO } from 'date-fns';
import { GRID_DATE_PICKER_POPPER_CLASS } from './datePickerConstants';
import { usePickerOpenCoordinator } from './usePickerOpenCoordinator';

const StyledDateRangePicker = styled(DateRangePicker, {
  shouldForwardProp: (prop) => prop !== 'isEditableText',
})<{ isEditableText?: boolean }>(({ theme, isEditableText }) =>
  isEditableText
    ? {
        '& .MuiPickersSectionList-root': { padding: 8 },
        '& .MuiOutlinedInput-root': { paddingRight: 0 },
        '& .MuiInputBase-root input': {
          padding: '8.5px 14px',
          paddingLeft: theme.spacing(0.5),
          display: 'flex',
          alignItems: 'center',
        },
        '.MuiInputBase-root': {
          marginLeft: theme.spacing(-1),
          paddingLeft: theme.spacing(0.5),
          width: '100%',
        },
        '.MuiInputBase-input': {
          paddingLeft: theme.spacing(0.5),
        },
        '.MuiInputBase-input::-webkit-input-placeholder': {
          visibility: 'hidden',
        },
        '&:hover .MuiInputBase-input::-webkit-input-placeholder': {
          visibility: 'visible',
        },
        '& fieldset': {
          opacity: 0,
        },
        '&:hover fieldset': {
          opacity: 1,
        },
        '& .MuiInputAdornment-root': {
          display: 'none',
        },
      }
    : {
        '& .MuiPickersInputBase-root': { paddingLeft: 0 },
        '& .MuiPickersSectionList-root': { padding: '8.5px 14px' },
      },
);

export const DateRangeInput = ({
  values,
  onChange,
  isEditableText,
  onClose,
  ...rest
}: {
  values: string[];
  onChange: (value: DateRange<Date>) => void;
  isEditableText?: boolean;
  autoFocus?: boolean;
  onClose?: () => void;
}) => {
  const startDate = values[0] ? parseISO(values[0]) : null;
  const endDate = values[1] ? parseISO(values[1]) : null;
  const coordinator = usePickerOpenCoordinator(rest.autoFocus);

  const handleClose = () => {
    coordinator.onClose();
    onClose?.();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <StyledDateRangePicker
        className="fs-exclude"
        // This magic is needed to get a data-testid properly assigned
        slotProps={{
          textField: {
            inputProps: {
              'data-testid': 'fim3-custom-field-input-date-range',
              'data-dd-action-name': 'FIM 3 Custom Field - date picker redacted',
            },
          },
          popper: { className: GRID_DATE_PICKER_POPPER_CLASS },
          // In grid-edit mode the calendar portals to body while the cell
          // wrapper manages focus on its own. Without this, MUI's focus trap
          // fights the grid for focus and overflows the call stack.
          desktopTrapFocus: { disableEnforceFocus: isEditableText },
        }}
        slots={{ field: SingleInputDateRangeField }}
        calendars={2}
        value={[startDate, endDate]}
        // reduce validation noise with onAccept event instead of onChange event
        // doesn't work changing back to onChange
        // broken in roadmunk too
        onChange={(rangeValue) => {
          onChange([rangeValue[0], rangeValue[1]]);
        }}
        isEditableText={isEditableText}
        {...rest}
        open={coordinator.open}
        onOpen={coordinator.onOpen}
        onClose={handleClose}
      />
    </LocalizationProvider>
  );
};
